import React, { useState } from "react";
import {
	ChevronDown,
	ChevronUp,
	Calendar,
	Users,
	Code,
	Building2,
	Globe,
	Headphones,
	BookOpen,
	Smartphone,
	Play,
	GraduationCap,
	Zap,
	FileText,
} from "lucide-react";
import { projectsData } from "../data/portfolioData";

const Projects = () => {
	const [expandedProject, setExpandedProject] = useState(null);

	const toggleProject = (projectId) => {
		setExpandedProject(expandedProject === projectId ? null : projectId);
	};

	// Function to get project image based on project ID
	const getProjectImage = (projectId) => {
		const imageMap = {
			1: "/WebDev_PAGE2.jpg", // Web development
			2: "/portfolio1.jpg", // Virtual simulations
			3: "/portfolio2.jpg", // Digital classroom activities
			4: "/DashbudMain01.png", // Software development
			5: "/portfolio3.jpg", // Traditional eLearning activities
			6: "/video04.jpg", // Video and audio
			7: "/portfolio5.jpg", // Virtual activities
			8: "/micro02.jpg", // Micro learning modules
			9: "/portfolio7.jpg", // App development
			10: "/intpdf02.jpg", // Interactive digital workbooks
		};
		return imageMap[projectId] || null;
	};

	// Function to get project icon based on project ID
	const getProjectIcon = (projectId) => {
		const iconMap = {
			1: Globe, // Web development
			2: Play, // Virtual simulations (VR/AR)
			3: GraduationCap, // Digital classroom activities
			4: Code, // Software development
			5: BookOpen, // Traditional eLearning activities
			6: Headphones, // Video and audio
			7: Users, // Virtual activities (collaboration)
			8: Zap, // Micro learning modules (quick/fast)
			9: Smartphone, // App development
			10: FileText, // Interactive digital workbooks
		};
		return iconMap[projectId] || Code;
	};

	return (
		<section className="min-h-screen bg-black py-12 sm:py-16 md:py-20">
			<div className="max-w-7xl mx-auto px-4 sm:px-6">
				{/* Mobile Navigation Link */}
				<div className="md:hidden mb-6">
					<a
						href="/about"
						className="inline-flex items-center text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
					>
						<span className="mr-2">←</span>
						About Me
					</a>
				</div>

				{/* Header */}
				<div className="mb-12 sm:mb-16">
					<h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
						Building with{" "}
						<span className="bg-gradient-to-r from-orange-700 to-orange-500 bg-clip-text text-transparent">
							modern tools
						</span>{" "}
						and{" "}
						<span className="bg-gradient-to-r from-orange-700 to-orange-500 bg-clip-text text-transparent">
							thoughtful design
						</span>
					</h1>

					{/* Filter Tags */}
					{/* <div className="flex flex-wrap gap-2 sm:gap-3 mt-6 sm:mt-8">
						{[
							"All Industries",
							"Web Development",
							"VR/AR",
							"Mobile Apps",
							"E-Learning",
						].map((tag, index) => (
							<button
								key={tag}
								className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 ${
									index === 0
										? "bg-white text-black"
										: "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white"
								}`}
							>
								{tag}
							</button>
						))}
					</div> */}
				</div>

				{/* Projects Table */}
				<div className="space-y-0">
					{projectsData.map((project, index) => (
						<div key={project.id} className="border-b border-gray-800">
							{/* Project Row */}
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-6 sm:py-8 hover:bg-gray-900/30 transition-colors duration-200 space-y-4 sm:space-y-0">
								<div className="flex items-start sm:items-center space-x-4 sm:space-x-8 flex-1">
									{/* Project Logo/Icon */}
									<div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-700 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
										{(() => {
											const IconComponent = getProjectIcon(project.id);
											return (
												<IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
											);
										})()}
									</div>

									{/* Project Info */}
									<div className="flex-1 min-w-0">
										<h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
											{project.name}
										</h3>
										<p className="text-gray-400 text-sm mb-2 line-clamp-2">
											{project.description}
										</p>
										<div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
											<span className="flex items-center">
												<Calendar className="w-3 h-3 mr-1" />
												{project.year}
											</span>
											<span className="hidden sm:inline">{project.client}</span>
											<span className="px-2 py-1 bg-gray-800 rounded">
												{project.category}
											</span>
										</div>
									</div>
								</div>

								{/* Project Types */}
								<div className="hidden lg:flex flex-col items-end space-y-1 mr-8">
									<div className="text-sm text-gray-400">Services</div>
									<div className="flex flex-col items-end space-y-1">
										{project.technologies.slice(0, 2).map((tech) => (
											<span key={tech} className="text-sm text-white">
												{tech}
											</span>
										))}
									</div>
								</div>

								{/* Find Out More Button */}
								<button
									onClick={() => toggleProject(project.id)}
									className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-700 to-orange-500 text-white rounded-full hover:from-orange-800 hover:to-orange-600 transition-all duration-200 font-medium text-sm sm:text-base w-full sm:w-auto"
								>
									<span>Find out more</span>
									{expandedProject === project.id ? (
										<ChevronUp className="w-4 h-4" />
									) : (
										<ChevronDown className="w-4 h-4" />
									)}
								</button>
							</div>

							{/* Expanded Details */}
							{expandedProject === project.id && (
								<div className="pb-6 sm:pb-8 px-2 sm:px-4 bg-gray-900/20 border-t border-gray-800">
									<div className="pt-6 sm:pt-8 space-y-6 sm:space-y-8">
										{/* Project Image */}
										<div className="w-full">
											<div className="relative w-full h-48 sm:h-64 md:h-80 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden border border-gray-700 group">
												{/* Show actual image if available, placeholder otherwise */}
												{getProjectImage(project.id) ? (
													<img
														src={getProjectImage(project.id)}
														alt={`${project.name} screenshot`}
														className={`w-full h-full ${
															project.id === 6 || project.id === 10
																? "object-cover"
																: "object-none scale-110 animate-pan-loop"
														}`}
													/>
												) : (
													/* Placeholder Image for projects without images */
													<div className="absolute inset-0 flex items-center justify-center">
														<div className="text-center">
															<div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-700 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
																<Code className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
															</div>
															<p className="text-gray-400 text-sm">
																Project Screenshot
															</p>
															<p className="text-gray-500 text-xs mt-1">
																{project.name}
															</p>
														</div>
													</div>
												)}

												{/* Overlay gradient for better text readability */}
												<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
											</div>
										</div>

										{/* Project Details Grid */}
										<div className="grid md:grid-cols-2 gap-6 sm:gap-8">
											{/* Left Column */}
											<div className="space-y-4 sm:space-y-6">
												<div>
													<h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">
														Project Overview
													</h4>
													<p className="text-gray-300 leading-relaxed text-sm sm:text-base">
														{project.details.challenges}
													</p>
												</div>

												<div>
													<h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">
														Key Features
													</h4>
													<ul className="space-y-2">
														{project.details.features.map((feature, idx) => (
															<li
																key={idx}
																className="flex items-center text-gray-300 text-sm sm:text-base"
															>
																<div className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
																{feature}
															</li>
														))}
													</ul>
												</div>
											</div>

											{/* Right Column */}
											<div className="space-y-4 sm:space-y-6">
												<div className="grid grid-cols-2 gap-3 sm:gap-4">
													<div className="bg-gray-800/50 p-3 sm:p-4 rounded-lg">
														<div className="flex items-center mb-2">
															<Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 mr-2" />
															<span className="text-xs sm:text-sm text-gray-400">
																Duration
															</span>
														</div>
														<span className="text-white font-medium text-sm sm:text-base">
															{project.details.duration}
														</span>
													</div>
													<div className="bg-gray-800/50 p-3 sm:p-4 rounded-lg">
														<div className="flex items-center mb-2">
															<Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400 mr-2" />
															<span className="text-xs sm:text-sm text-gray-400">
																Client Sector
															</span>
														</div>
														<span className="text-white font-medium text-sm sm:text-base">
															{project.clientSector}
														</span>
													</div>
												</div>

												{/* <div>
													<h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">
														Results
													</h4>
													<p className="text-gray-300 leading-relaxed text-sm sm:text-base">
														{project.details.results}
													</p>
												</div> */}

												<div>
													<h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">
														Technologies
													</h4>
													<div className="flex flex-wrap gap-2">
														{project.technologies.map((tech) => (
															<span
																key={tech}
																className="px-2 sm:px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs sm:text-sm"
															>
																{tech}
															</span>
														))}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Projects;
